/*
 *   File : portfolio-health.js
 *   Author : https://evoqins.com
 *   Description : Portfolio health dashboard shown after the MF Central import
 *   Integrations : NA
 *   Version : 1.0.0
*/

// *********** React Core ***************//
import { useEffect, useState } from "react";

// *********** Third Party ***************//
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

// *********** Components ***************//
import { PrimaryButton } from "../../Component/Buttons";
import { APIService } from "../../Service";

// *********** Assets ***************//
import Loader from "../../Assets/Images/coloured_loader.svg";
import LossArrow from "../../Assets/Images/new-onboarding/portfolio-health/loss-arrow.svg";
import GainArrow from "../../Assets/Images/new-onboarding/portfolio-health/gain-arrow.svg";
import TickSquare from "../../Assets/Images/new-onboarding/portfolio-health/tick-square.svg";
import GaugeArcRed from "../../Assets/Images/new-onboarding/portfolio-health/gauge-arc-red.svg";
import GaugeArcYellow from "../../Assets/Images/new-onboarding/portfolio-health/gauge-arc-yellow.svg";
import GaugeArcGreen from "../../Assets/Images/new-onboarding/portfolio-health/gauge-arc-green.svg";
import GaugeArcDotted from "../../Assets/Images/new-onboarding/portfolio-health/gauge-arc-dotted.svg";

// Static text, the API does not send these
const HEALTH_DESCRIPTION = "A few issues below are likely costing you money over time - here is exactly why.";

const EMPTY_PORTFOLIO_DESCRIPTION = "It seems there is no investment data available yet. Start investing in a few simple steps and we will check your portfolio health for you.";

const INVESTING_STEPS = [
    { title: "Choose the right funds", description: "Explore low-cost index funds or ready-made baskets that match your goal." },
    { title: "Set your investment amount", description: "Decide how much you want to invest. You can start small and add more later." },
    { title: "Start a SIP or invest Lumpsum", description: "Invest every month with a SIP or put in a one-time amount, whichever suits you." },
];

// Mirrors the sections of the health dashboard the user unlocks after investing
const HEALTH_CHECK_PREVIEW = [
    { title: "Health score", description: "One score that sums up how well your portfolio is built." },
    { title: "Expense leakage", description: "How much higher fund costs could take from your returns." },
    { title: "Fund overlap", description: "Whether your funds hold the same stocks again and again." },
    { title: "Risk meter", description: "How aggressive or conservative your portfolio really is." },
];

// Score colour follows the flag sent by the API
const HEALTH_FLAG_CLASSES = {
    red: "e-text-fuzzy-wuzzy-red",
    orange: "e-text-orange-peel",
    green: "e-text-iguana-green",
};

// The API sends the long term cost for 20 years (extra_cost_long_term_20y)
const EXPENSE_LONG_TERM_YEARS = 20;

// The knob and the label colour follow the band sent by the API
const RISK_BANDS = {
    conservative: { knob_class: "health-gauge-knob-low", text_class: "e-text-iguana-green" },
    moderate: { knob_class: "health-gauge-knob-medium", text_class: "e-text-orange-yellow" },
    aggressive: { knob_class: "health-gauge-knob-high", text_class: "e-text-fuzzy-wuzzy-red" },
};

const DISCLAIMER = "Illustrative comparison using historical index performance, based on holdings fetched via MF Central. Past performance doesn't guarantee future returns; your existing investments may carry different risk, tax, or exit considerations. This isn't personalized financial advice.";

// Donut chart drawn on a 100 x 100 box, each circle has pathLength 100 so a type length is its percentage
const DONUT_RADIUS = 43;
const DONUT_STROKE_WIDTH = 14;
const DONUT_SEGMENT_GAP = 0.55;

const PortfolioHealth = () => {

    // Router
    const navigate = useNavigate();

    // State
    const [loading, setLoading] = useState(true);
    const [healthData, setHealthData] = useState({});
    // The API answers 400 when the user has no investments yet
    const [isPortfolioEmpty, setIsPortfolioEmpty] = useState(false);

    useEffect(() => {
        _getPortfolioHealth();
    }, []);

    // Go to explore to pick better funds
    const _handleStartClosingGap = () => {
        navigate("/explore");
    };

    // Go back to the dashboard
    const _handleExploreDashboard = () => {
        navigate("/");
    };

    // Go to explore to make the first investment
    const _handleStartInvesting = () => {
        navigate("/explore");
    };

    // API - Get portfolio health
    const _getPortfolioHealth = () => {
        let is_post = true;
        let url = "portfolio/health";
        let data = {};
        let api_version = 2;

        setLoading(true);
        APIService(is_post, url, data, null, api_version).then((response) => {
            if (response.status_code === 200) {
                setHealthData(response.data);
            } else if (response.status_code === 400) {
                setHealthData({});
                setIsPortfolioEmpty(true);
            } else {
                setHealthData({});
                toast.dismiss();
                toast.error(response.message);
            }
            setLoading(false);
        });
    };

    // *********** Inner Components ***************//
    const TickCard = ({ data }) => {
        return (
            <li className="e-bg-health-recommendation e-border-1px-mint-frost e-border-radius-12 px-4 py-3">
                <h3 className="d-flex align-items-center gap-2 e-text-charleston-green e-primary-font-inter e-font-weight-600 e-font-15 mb-2">
                    <span className="health-tick d-flex flex-shrink-0 align-items-center justify-content-center">
                        <img src={TickSquare}
                            width={13}
                            height={13}
                            draggable="false"
                            alt="" />
                    </span>
                    {data.title}
                </h3>
                <p className="e-text-cool-gray e-primary-font-inter e-font-weight-500 e-font-14 line-height-18px mb-0">
                    {data.description}
                </p>
            </li>
        );
    };

    return (
        loading === true ? (
            <section className="row px-4 px-sm-5 ps-lg-4 pe-lg-0">
                <div className="col-lg-11 e-bg-lotion e-border-radius-32 padding-24px-all mb-4 min-h-100vh text-center">
                    <img src={Loader}
                        className="mt-5 pt-5"
                        draggable="false"
                        alt="loader" />
                </div>
            </section>
        ) : isPortfolioEmpty === true ? (
            <section className="row px-4 px-sm-5 ps-lg-4 pe-lg-0">
                <div className="col-lg-11 e-bg-lotion e-border-radius-32 padding-24px-all mb-4">

                    {/* No investment message */}
                    <article className="e-bg-snow-gray e-border-1px-faint-ink e-border-radius-28 padding-8px-all mb-4">
                        <div className="e-bg-health-banner e-border-1px-faint-ink e-border-radius-20 px-4 py-4">
                            <h2 className="e-text-midnight-navy e-primary-font-inter e-font-weight-700 e-font-24 line-height-36px letter-spacing-neg-2per mb-1">
                                Your Portfolio Health
                            </h2>
                            <p className="e-text-cool-gray e-primary-font-inter e-font-weight-400 e-font-14 line-height-22px mb-0 col-lg-8">
                                {EMPTY_PORTFOLIO_DESCRIPTION}
                            </p>
                        </div>
                    </article>

                    {/* Health check preview */}
                    <section className="mb-4">
                        <h2 className="e-text-charleston-green e-alt-font-poppins e-font-weight-600 e-font-16 line-height-24px mb-1">
                            What your health check will show
                        </h2>
                        <p className="e-text-cool-gray e-primary-font-inter e-font-weight-400 e-font-14 line-height-22px mb-3">
                            Once you invest, we check your portfolio and show you where it can do better.
                        </p>
                        <ul className="row g-3 list-unstyled mb-0">
                            {
                                HEALTH_CHECK_PREVIEW.map((item) => (
                                    <li className="col-12 col-sm-6 col-xl-3" key={item.title}>
                                        <article className="e-bg-snow-gray e-border-1px-faint-ink e-border-radius-16 padding-24px-all h-100">
                                            <h3 className="e-text-eerie-black e-primary-font-inter e-font-weight-600 e-font-15 mb-2">
                                                {item.title}
                                            </h3>
                                            <p className="e-text-onyx e-alt-font-poppins e-font-weight-400 e-font-12 line-height-18px mb-0">
                                                {item.description}
                                            </p>
                                        </article>
                                    </li>
                                ))
                            }
                        </ul>
                    </section>

                    {/* Investing steps */}
                    <article className="e-bg-white e-border-1px-platinum-blue e-border-radius-16 padding-24px-all mb-4">
                        <h2 className="e-text-eerie-black e-primary-font-inter e-font-weight-500 e-font-18 line-height-24px mb-3">
                            Start investing in 3 simple steps
                        </h2>
                        <ol className="d-flex flex-column gap-3 list-unstyled mb-0">
                            {
                                INVESTING_STEPS.map((item) => (
                                    <TickCard key={item.title} data={item} />
                                ))
                            }
                        </ol>
                    </article>

                    {/* Actions */}
                    <div className="e-bg-white text-center pt-3 pb-4">
                        <div className="col-sm-8 col-md-6 col-xl-4 mx-auto">
                            <PrimaryButton name="Start investing"
                                className="w-100 padding-12px-tb e-font-14"
                                onPress={_handleStartInvesting} />
                        </div>
                        <p className="e-font-12 e-alt-font-poppins line-height-17px mt-3 mb-0">
                            <button type="button"
                                className="e-bg-transparent no-border no-outline p-0 e-text-egyptian-blue e-font-weight-600 cursor-pointer"
                                onClick={_handleExploreDashboard}>
                                Explore the dashboard
                            </button>
                        </p>
                    </div>
                </div>
            </section>
        ) : (
            <section className="row px-4 px-sm-5 ps-lg-4 pe-lg-0">
                <div className="col-lg-11 e-bg-lotion e-border-radius-32 padding-24px-all mb-4">

                    {/* Health score */}
                    <article className="e-bg-snow-gray e-border-1px-faint-ink e-border-radius-28 padding-8px-all mb-4">
                        <div className="e-bg-health-banner e-border-1px-faint-ink e-border-radius-20 d-flex justify-content-between align-items-center gap-3 px-4 py-4">
                            <div>
                                <h2 className="e-text-midnight-navy e-primary-font-inter e-font-weight-700 e-font-24 line-height-36px letter-spacing-neg-2per mb-1">
                                    Your Portfolio Health
                                </h2>
                                <p className="e-text-cool-gray e-primary-font-inter e-font-weight-400 e-font-14 line-height-22px mb-0">
                                    {HEALTH_DESCRIPTION}
                                </p>
                            </div>
                            <p className="d-flex align-items-baseline flex-shrink-0 e-primary-font-inter mb-0"
                                aria-label={`Score ${healthData.health_score?.score} out of ${healthData.health_score?.max_score}, ${healthData.health_score?.label}`}>
                                <span className={`health-score-number ${HEALTH_FLAG_CLASSES[healthData.health_score?.flag] ?? HEALTH_FLAG_CLASSES.orange} e-font-weight-700 e-font-56 line-height-64px`}>
                                    {healthData.health_score?.score}
                                </span>
                                <span className="e-text-midnight-navy e-font-weight-600 e-font-32 line-height-40px">
                                    /{healthData.health_score?.max_score}
                                </span>
                            </p>
                        </div>
                    </article>

                    <div className="row g-4 mb-4">
                        <div className="col-lg-6 d-flex flex-column gap-4">

                            {/* Portfolio summary */}
                            <article className="e-bg-snow-gray e-border-1px-faint-ink e-border-radius-24 padding-24px-all">
                                <dl className="d-flex flex-column gap-3 mb-0">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <dt className="e-text-onyx e-alt-font-poppins e-font-weight-400 e-font-12 line-height-18px">
                                            Profit / Loss
                                        </dt>
                                        <dd className={`d-flex align-items-center gap-1 ${healthData.investment_summary?.profit_loss < 0 ? "e-text-radical-red" : "e-text-bottle-green"} e-primary-font-inter e-font-weight-600 e-font-18 line-height-24px mb-0`}>
                                            <img src={healthData.investment_summary?.profit_loss < 0 ? LossArrow : GainArrow}
                                                width={16}
                                                height={16}
                                                className={healthData.investment_summary?.profit_loss < 0 ? "transform-rotate-180" : ""}
                                                draggable="false"
                                                alt="" />
                                            {/* The text may already carry a minus sign, the sign is added again from the number */}
                                            {healthData.investment_summary?.profit_loss < 0 ? "-" : "+"}{healthData.investment_summary?.profit_loss_text?.replace("-", "")}
                                        </dd>
                                    </div>
                                    <div className="d-flex justify-content-between align-items-center">
                                        <dt className="e-text-onyx e-alt-font-poppins e-font-weight-400 e-font-12 line-height-18px">
                                            Current value
                                        </dt>
                                        <dd className="e-text-eerie-black e-primary-font-inter e-font-weight-600 e-font-18 line-height-24px mb-0">
                                            {healthData.investment_summary?.current_value_text}
                                        </dd>
                                    </div>
                                    <div className="d-flex justify-content-between align-items-center">
                                        <dt className="e-text-onyx e-alt-font-poppins e-font-weight-400 e-font-12 line-height-18px">
                                            Absolute return
                                        </dt>
                                        <dd className="e-text-eerie-black e-primary-font-inter e-font-weight-600 e-font-18 line-height-24px mb-0">
                                            {healthData.investment_summary?.absolute_return_pct_text}
                                        </dd>
                                    </div>
                                    <div className="d-flex justify-content-between align-items-center">
                                        <dt className="e-text-onyx e-alt-font-poppins e-font-weight-400 e-font-12 line-height-18px">
                                            Total Invested
                                        </dt>
                                        <dd className="e-text-eerie-black e-primary-font-inter e-font-weight-600 e-font-18 line-height-24px mb-0">
                                            {healthData.investment_summary?.total_invested_text}
                                        </dd>
                                    </div>
                                </dl>
                            </article>

                            {/* Diversification score */}
                            <article className="e-border-1px-platinum-blue e-border-radius-16 padding-24px-all">
                                <h2 className="e-text-eerie-black e-primary-font-inter e-font-weight-500 e-font-18 line-height-24px mb-3">
                                    Diversification score
                                </h2>
                                <div className="position-relative d-flex justify-content-center">
                                    <svg width={208}
                                        height={208}
                                        viewBox="0 0 100 100"
                                        role="img"
                                        aria-label="Diversification by fund type">
                                        {
                                            // Each arc starts where the previous types end, a type with 0% gets no arc
                                            healthData.diversification_score?.breakdown?.map((item, index) => (
                                                <circle key={item.type}
                                                    cx={50}
                                                    cy={50}
                                                    r={DONUT_RADIUS}
                                                    fill="none"
                                                    stroke={item.color}
                                                    strokeWidth={DONUT_STROKE_WIDTH}
                                                    pathLength={100}
                                                    strokeDasharray={`${Math.max(item.percentage - DONUT_SEGMENT_GAP, 0)} 100`}
                                                    strokeDashoffset={-healthData.diversification_score.breakdown.slice(0, index).reduce((total, previous) => total + previous.percentage, 0)}
                                                    transform="rotate(-90 50 50)" />
                                            ))
                                        }
                                    </svg>
                                    <div className="position-absolute top-50 start-50 translate-middle text-center">
                                        <p className="e-text-dim-gray e-primary-font-inter e-font-weight-500 e-font-12 line-height-16px mb-1">
                                            Total funds
                                        </p>
                                        <p className="e-text-jet-black e-primary-font-inter e-font-weight-600 e-font-28 line-height-32px mb-0">
                                            {healthData.diversification_score?.total_funds}
                                        </p>
                                    </div>
                                </div>
                                <table className="w-100 mt-4 e-text-onyx e-primary-font-inter e-font-weight-400 e-font-12 line-height-20px">
                                    <thead>
                                        <tr className="text-start">
                                            <th className="e-border-bottom-1px-platinum-blue pb-2 e-font-weight-400" scope="col">Type</th>
                                            <th className="e-border-bottom-1px-platinum-blue pb-2 e-font-weight-400" scope="col">No of Fund</th>
                                            <th className="e-border-bottom-1px-platinum-blue pb-2 e-font-weight-400" scope="col">Percentage</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {
                                            healthData.diversification_score?.breakdown?.map((item) => (
                                                <tr key={item.type}>
                                                    <td className="padding-6px-tb">
                                                        <span className="d-flex align-items-center gap-1">
                                                            <span className="health-legend-dot rounded-circle flex-shrink-0"
                                                                style={{ backgroundColor: item.color }} />
                                                            {item.type}
                                                        </span>
                                                    </td>
                                                    <td className="padding-6px-tb e-font-weight-500">{item.fund_count}</td>
                                                    <td className="padding-6px-tb e-font-weight-500">{item.percentage_text}</td>
                                                </tr>
                                            ))
                                        }
                                    </tbody>
                                </table>
                                <p className="e-text-smoky-black-60 e-alt-font-poppins e-font-weight-400 e-font-12 line-height-18px mt-4 mb-0">
                                    {healthData.diversification_score?.insight}
                                </p>
                            </article>
                        </div>

                        <div className="col-lg-6 d-flex flex-column gap-4">

                            {/* Expense leakage */}
                            <article className="e-bg-white e-border-1px-platinum-blue e-border-radius-16 padding-24px-all">
                                <h2 className="e-text-eerie-black e-primary-font-inter e-font-weight-500 e-font-18 line-height-24px mb-1">
                                    Expense leakage
                                </h2>
                                <dl className="mb-1">
                                    <div className="e-border-bottom-1px-platinum-blue d-flex justify-content-between align-items-center gap-3 padding-12px-tb">
                                        <dt className="e-text-navy-80per e-primary-font-inter e-font-weight-400 e-font-14 line-height-18px">
                                            Weighted expense ratio
                                        </dt>
                                        <dd className="e-text-navy-90per e-primary-font-inter e-font-weight-700 e-font-18 line-height-24px mb-0">
                                            {healthData.expense_leakage?.weighted_expense_ratio_text}
                                        </dd>
                                    </div>
                                    <div className="d-flex justify-content-between align-items-center gap-3 padding-12px-tb">
                                        <dt className="e-text-navy-80per e-primary-font-inter e-font-weight-400 e-font-14 line-height-18px">
                                            Ideal expense ratio
                                        </dt>
                                        <dd className="e-text-navy-90per e-primary-font-inter e-font-weight-700 e-font-18 line-height-24px mb-0">
                                            {healthData.expense_leakage?.ideal_expense_ratio_text}
                                        </dd>
                                    </div>
                                </dl>
                                <p className="e-text-storm-gray e-primary-font-inter e-font-weight-400 e-font-12 line-height-18px mb-0">
                                    You may be paying approximately <span className="e-font-weight-700">{healthData.expense_leakage?.extra_cost_per_year_text}</span> more per year, roughly <span className="e-font-weight-700">{healthData.expense_leakage?.extra_cost_long_term_20y_text}</span> over the long term ({EXPENSE_LONG_TERM_YEARS} years), due to higher expense ratios
                                </p>
                            </article>

                            {/* Fund overlap */}
                            <article className="e-bg-rose-blush e-border-radius-4 d-flex justify-content-between align-items-start gap-3 px-4 py-3">
                                <div>
                                    <h2 className="e-text-brick-red e-primary-font-inter e-font-weight-500 e-font-16 line-height-18px margin-12px-bottom">
                                        Fund overlap
                                    </h2>
                                    <p className="e-text-dusty-rose e-primary-font-inter e-font-weight-500 e-font-12 line-height-16px mb-0">
                                        {healthData.fund_overlap?.insight}
                                    </p>
                                </div>
                                <p className="e-text-brick-red e-primary-font-inter e-font-weight-700 e-font-16 line-height-18px mb-0">
                                    {healthData.fund_overlap?.overlap_pct_text}
                                </p>
                            </article>

                            {/* Risk meter */}
                            <section>
                                <h2 className="e-text-eerie-black e-primary-font-inter e-font-weight-500 e-font-18 line-height-24px mb-3">
                                    Risk meter
                                </h2>
                                <div className="e-bg-cultured e-border-radius-16 d-flex justify-content-center padding-24px-tb">
                                    <div className="health-gauge">
                                        <div className="health-gauge-square">
                                            <img src={GaugeArcRed}
                                                className="health-gauge-arc health-gauge-arc-red"
                                                draggable="false"
                                                alt="" />
                                            <img src={GaugeArcYellow}
                                                className="health-gauge-arc health-gauge-arc-yellow"
                                                draggable="false"
                                                alt="" />
                                            <img src={GaugeArcGreen}
                                                className="health-gauge-arc health-gauge-arc-green"
                                                draggable="false"
                                                alt="" />
                                            <img src={GaugeArcDotted}
                                                className="health-gauge-arc health-gauge-arc-dotted"
                                                draggable="false"
                                                alt="" />
                                            {
                                                RISK_BANDS[healthData.risk_meter?.band] &&
                                                <span className={`health-gauge-knob ${RISK_BANDS[healthData.risk_meter.band].knob_class}`} />
                                            }
                                        </div>
                                        {
                                            RISK_BANDS[healthData.risk_meter?.band] &&
                                            <p className={`health-gauge-label ${RISK_BANDS[healthData.risk_meter.band].text_class} e-alt-font-poppins e-font-weight-600 e-font-16 line-height-24px text-center mb-0`}>
                                                {healthData.risk_meter.label}
                                            </p>
                                        }
                                    </div>
                                </div>
                            </section>

                            {/* Benchmark comparison */}
                            <section>
                                <h2 className="e-text-eerie-black e-primary-font-inter e-font-weight-500 e-font-18 line-height-24px mb-3">
                                    Benchmark comparison
                                </h2>
                                <div className="e-bg-rose-blush e-border-radius-16 padding-24px-all">
                                    <p className="e-text-charleston-green e-primary-font-inter e-font-weight-600 e-font-14 line-height-20px mb-2">
                                        Your portfolio vs {healthData.benchmark_comparison?.benchmark_name}
                                    </p>
                                    <p className="e-text-dusty-rose e-primary-font-inter e-font-weight-500 e-font-12 line-height-16px mb-0">
                                        Underperforming {healthData.benchmark_comparison?.benchmark_name} by {healthData.benchmark_comparison?.coverage_pct_text} points
                                    </p>
                                </div>
                            </section>
                        </div>
                    </div>

                    {/* Recommendations */}
                    <section className="mb-4">
                        <h2 className="e-text-charleston-green e-alt-font-poppins e-font-weight-600 e-font-16 line-height-24px mb-3">
                            Recommendations for you
                        </h2>
                        <ul className="d-flex flex-column gap-3 list-unstyled mb-0">
                            {
                                // The API sends no id, the title is unique for each recommendation
                                healthData.recommendations?.map((item) => (
                                    <TickCard key={item.title} data={item} />
                                ))
                            }
                        </ul>
                    </section>

                    <p className="e-text-smoky-black-60 e-alt-font-poppins e-font-weight-400 e-font-12 line-height-18px mb-4">
                        {DISCLAIMER}
                    </p>

                    {/* Actions */}
                    <div className="e-bg-white text-center pt-3 pb-4">
                        <div className="col-sm-8 col-md-6 col-xl-4 mx-auto">
                            <PrimaryButton name="Start closing the gap"
                                className="w-100 padding-12px-tb e-font-14"
                                onPress={_handleStartClosingGap} />
                        </div>
                        <p className="d-flex justify-content-center gap-2 e-font-12 e-alt-font-poppins line-height-17px mt-3 mb-0">
                            <span className="e-text-charleston-green e-font-weight-500">Not convinced yet?</span>
                            <button type="button"
                                className="e-bg-transparent no-border no-outline p-0 e-text-egyptian-blue e-font-weight-600 cursor-pointer"
                                onClick={_handleExploreDashboard}>
                                Explore the dashboard
                            </button>
                        </p>
                    </div>
                </div>
            </section>
        )
    );
};

export default PortfolioHealth;
